import axios from 'axios';

import { http } from './http';
import { API_V1_BASE } from '../config/api.config';
import { postBulkAction } from './bulk.service';
import { unwrapData, unwrapList } from '../utils/unwrapApiData';
import { normalizeAdminSummaryPayload } from '../utils/organizationOverview';

/** کلاینت بدون Bearer — فقط برای endpoint عمومی دعوت */
const publicHttp = axios.create({
  baseURL: API_V1_BASE,
  headers: { Accept: 'application/json', 'Content-Type': 'application/json' },
  timeout: 60_000,
});

/**
 * هدر چندسازمانی — بک‌اند انتظار `X-Organization-ID` دارد.
 * @param {string|number|null|undefined} organizationId
 * @param {import('axios').AxiosRequestConfig} [extra]
 */
export function withOrgTenant(organizationId, extra = {}) {
  const merged = { ...extra };
  if (organizationId != null && organizationId !== '') {
    const prev = extra.headers && typeof extra.headers === 'object' ? extra.headers : {};
    merged.headers = { ...prev, 'X-Organization-ID': String(organizationId) };
  }
  return merged;
}

/**
 * نرمال‌سازی لیست سازمان‌ها — پشتیبانی از آرایهٔ ساده یا paginator لاراول.
 * فقط یک لایهٔ `payload.data` باز می‌شود تا `unwrapData` آرایهٔ داخلی paginator را
 * به‌اشتباه به عنوان کل payload نگیرد و meta از دست نرود.
 *
 * @param {*} payload — معمولاً `response.data` از axios
 */
export function normalizeOrganizationsListResponse(payload) {
  const inner = payload && typeof payload === 'object' && 'data' in payload ? payload.data : payload;
  if (Array.isArray(inner)) {
    return { items: inner, page: 1, perPage: inner.length, total: inner.length, lastPage: 1 };
  }
  if (inner && typeof inner === 'object' && Array.isArray(inner.data)) {
    const meta = inner;
    return {
      items: inner.data,
      page: meta.current_page ?? meta.page ?? 1,
      perPage: meta.per_page ?? meta.perPage ?? 15,
      total: meta.total ?? inner.data.length,
      lastPage: meta.last_page ?? meta.lastPage ?? 1,
    };
  }
  return { items: [], page: 1, perPage: 15, total: 0, lastPage: 1 };
}

export async function listOrganizations(params = {}, config = {}) {
  const { data } = await http.get('/organizations', { ...config, params });
  return normalizeOrganizationsListResponse(data);
}

export async function createOrganization(body) {
  const { data } = await http.post('/organizations', body);
  return unwrapData(data) ?? data;
}

export async function getOrganization(id) {
  const { data } = await http.get(`/organizations/${id}`, withOrgTenant(id));
  return unwrapData(data) ?? data;
}

export async function getOrganizationAdminSummary(id) {
  const { data } = await http.get(`/organizations/${id}/admin-summary`, withOrgTenant(id));
  return normalizeAdminSummaryPayload(unwrapData(data) ?? data);
}

export async function getConfidentialityPolicies(organizationId) {
  const { data } = await http.get(
    `/organizations/${organizationId}/confidentiality-policies`,
    withOrgTenant(organizationId),
  );
  return unwrapData(data) ?? data;
}

export async function updateConfidentialityPolicies(organizationId, body) {
  const { data } = await http.put(
    `/organizations/${organizationId}/confidentiality-policies`,
    body,
    withOrgTenant(organizationId),
  );
  return unwrapData(data) ?? data;
}

export async function updateOrganization(id, body) {
  const { data } = await http.put(`/organizations/${id}`, body, withOrgTenant(id));
  return unwrapData(data) ?? data;
}

export async function deleteOrganization(id) {
  const { data } = await http.delete(`/organizations/${id}`, withOrgTenant(id));
  return data;
}

export async function bulkOrganizations(body) {
  return postBulkAction('/organizations/bulk', body);
}

export async function bulkOrganizationMembers(organizationId, body) {
  return postBulkAction(
    `/organizations/${organizationId}/members/bulk`,
    body,
    withOrgTenant(organizationId),
  );
}

export async function listMembers(organizationId, params = {}) {
  const { data } = await http.get(
    `/organizations/${organizationId}/members`,
    withOrgTenant(organizationId, { params }),
  );
  return unwrapList(data);
}

export async function listPositions(organizationId) {
  const { data } = await http.get(
    `/organizations/${organizationId}/positions`,
    withOrgTenant(organizationId),
  );
  return unwrapList(data);
}

export async function createPosition(organizationId, body) {
  const { data } = await http.post(
    `/organizations/${organizationId}/positions`,
    body,
    withOrgTenant(organizationId),
  );
  return unwrapData(data) ?? data;
}

export async function updatePosition(organizationId, positionId, body) {
  const { data } = await http.put(
    `/organizations/${organizationId}/positions/${positionId}`,
    body,
    withOrgTenant(organizationId),
  );
  return unwrapData(data) ?? data;
}

export async function deletePosition(organizationId, positionId) {
  const { data } = await http.delete(
    `/organizations/${organizationId}/positions/${positionId}`,
    withOrgTenant(organizationId),
  );
  return unwrapData(data) ?? data;
}

export async function syncPositionHolders(organizationId, positionId, userIds) {
  const { data } = await http.put(
    `/organizations/${organizationId}/positions/${positionId}/holders`,
    { user_ids: userIds },
    withOrgTenant(organizationId),
  );
  return unwrapData(data) ?? data;
}

export async function unassignMemberPosition(organizationId, userId, positionId) {
  const { data } = await http.delete(
    `/organizations/${organizationId}/members/${userId}/positions/${positionId}`,
    withOrgTenant(organizationId),
  );
  return unwrapData(data) ?? data;
}

export async function addMember(organizationId, body) {
  const { data } = await http.post(
    `/organizations/${organizationId}/members`,
    body,
    withOrgTenant(organizationId),
  );
  return unwrapData(data) ?? data;
}

export async function importMembers(organizationId, body) {
  const { data } = await http.post(
    `/organizations/${organizationId}/members/import`,
    body,
    withOrgTenant(organizationId),
  );
  return unwrapData(data) ?? data;
}

export async function updateMember(organizationId, userId, body) {
  const { data } = await http.put(
    `/organizations/${organizationId}/members/${userId}`,
    body,
    withOrgTenant(organizationId),
  );
  return unwrapData(data) ?? data;
}

export async function getMemberDeactivationImpact(organizationId, userId) {
  const { data } = await http.get(
    `/organizations/${organizationId}/members/${userId}/deactivation-impact`,
    withOrgTenant(organizationId),
  );
  return unwrapData(data) ?? data;
}

export async function deactivateMember(organizationId, userId, body = {}) {
  const { data } = await http.post(
    `/organizations/${organizationId}/members/${userId}/deactivate`,
    body,
    withOrgTenant(organizationId),
  );
  return unwrapData(data) ?? data;
}

export async function removeMember(organizationId, userId, params = {}) {
  const { data } = await http.delete(
    `/organizations/${organizationId}/members/${userId}`,
    withOrgTenant(organizationId, { params }),
  );
  return unwrapData(data) ?? data;
}

export async function loginAsMember(organizationId, userId, { reason } = {}) {
  const { data } = await http.post(
    `/organizations/${organizationId}/members/${userId}/login-as`,
    reason ? { reason } : {},
    withOrgTenant(organizationId),
  );
  const inner = unwrapData(data) ?? data;
  return {
    token: inner?.token ?? null,
    expires_in_minutes: inner?.expires_in_minutes,
    user: inner?.user,
  };
}

export async function getMemberPermissions(organizationId, userId) {
  const { data } = await http.get(
    `/organizations/${organizationId}/members/${userId}/permissions`,
    withOrgTenant(organizationId),
  );
  return unwrapData(data) ?? data;
}

export async function updateMemberPermissions(organizationId, userId, body) {
  const { data } = await http.put(
    `/organizations/${organizationId}/members/${userId}/permissions`,
    body,
    withOrgTenant(organizationId),
  );
  return unwrapData(data) ?? data;
}

export async function listDepartmentMembers(departmentId, organizationId, params = {}) {
  const { data } = await http.get(
    `/departments/${departmentId}/members`,
    {
      ...withOrgTenant(organizationId),
      params,
    },
  );
  return unwrapList(data);
}

export async function addDepartmentMember(departmentId, body, organizationId) {
  const { data } = await http.post(
    `/departments/${departmentId}/members`,
    body,
    withOrgTenant(organizationId),
  );
  return unwrapData(data) ?? data;
}

export async function removeDepartmentMember(departmentId, userId, organizationId) {
  const { data } = await http.delete(
    `/departments/${departmentId}/members/${userId}`,
    withOrgTenant(organizationId),
  );
  return unwrapData(data) ?? data;
}

export async function inviteUser(organizationId, body) {
  const { data } = await http.post(
    `/organizations/${organizationId}/invite`,
    body,
    withOrgTenant(organizationId),
  );
  return unwrapData(data) ?? data;
}

export async function listInvitations(organizationId, params = {}) {
  const { data } = await http.get(
    `/organizations/${organizationId}/invitations`,
    withOrgTenant(organizationId, { params }),
  );
  return unwrapList(data);
}

/** جزئیات دعوت — بدون احراز هویت */
export async function getInvitationByTokenPublic(token) {
  const safe = encodeURIComponent(token);
  const { data } = await publicHttp.get(`/invitations/${safe}`);
  return unwrapData(data) ?? data;
}

export async function acceptInvitation(token) {
  const safe = encodeURIComponent(token);
  const { data } = await http.post(`/invitations/${safe}/accept`);
  return unwrapData(data) ?? data;
}

export async function cancelInvitation(invitationId, organizationId) {
  const { data } = await http.delete(`/invitations/${invitationId}`, withOrgTenant(organizationId));
  return data;
}

export async function listDepartments(organizationId) {
  const { data } = await http.get(
    `/organizations/${organizationId}/departments`,
    withOrgTenant(organizationId),
  );
  return unwrapList(data);
}

export async function createDepartment(organizationId, body) {
  const { data } = await http.post(
    `/organizations/${organizationId}/departments`,
    body,
    withOrgTenant(organizationId),
  );
  return unwrapData(data) ?? data;
}

export async function getDepartment(id, organizationId) {
  const { data } = await http.get(`/departments/${id}`, withOrgTenant(organizationId));
  return unwrapData(data) ?? data;
}

export async function updateDepartment(id, body, organizationId) {
  const { data } = await http.put(`/departments/${id}`, body, withOrgTenant(organizationId));
  return unwrapData(data) ?? data;
}

export async function deleteDepartment(id, organizationId) {
  const { data } = await http.delete(`/departments/${id}`, withOrgTenant(organizationId));
  return data;
}

export async function listRoles(organizationId) {
  const { data } = await http.get(`/organizations/${organizationId}/roles`, withOrgTenant(organizationId));
  return unwrapList(data);
}

export async function createRole(organizationId, body) {
  const { data } = await http.post(
    `/organizations/${organizationId}/roles`,
    body,
    withOrgTenant(organizationId),
  );
  return unwrapData(data) ?? data;
}

export async function updateRole(roleId, body, organizationId) {
  const { data } = await http.put(`/roles/${roleId}`, body, withOrgTenant(organizationId));
  return unwrapData(data) ?? data;
}

export async function deleteRole(roleId, organizationId) {
  const { data } = await http.delete(`/roles/${roleId}`, withOrgTenant(organizationId));
  return data;
}

export async function duplicateRole(organizationId, roleId, body) {
  const { data } = await http.post(
    `/organizations/${organizationId}/roles/${roleId}/duplicate`,
    body,
    withOrgTenant(organizationId),
  );
  return unwrapData(data) ?? data;
}

export async function getOrgRolePermissions(organizationId, roleId) {
  const { data } = await http.get(
    `/organizations/${organizationId}/roles/${roleId}/permissions`,
    withOrgTenant(organizationId),
  );
  return unwrapData(data) ?? data;
}

export async function updateOrgRolePermissions(organizationId, roleId, body) {
  const { data } = await http.put(
    `/organizations/${organizationId}/roles/${roleId}/permissions`,
    body,
    withOrgTenant(organizationId),
  );
  return unwrapData(data) ?? data;
}

/** مجوزهای قابل انتساب به نقش‌های سازمان — `GET /permissions?scope=organization` */
export async function listOrganizationPermissions(organizationId) {
  const cfg =
    organizationId != null && organizationId !== ''
      ? withOrgTenant(organizationId, { params: { scope: 'organization' } })
      : { params: { scope: 'organization' } };
  const { data } = await http.get('/permissions', cfg);
  return unwrapList(data);
}

/** @deprecated از listOrganizationPermissions استفاده کنید */
export async function listPermissions(organizationId) {
  return listOrganizationPermissions(organizationId);
}
